// Settings page — replaces the old Simulator tab. Houses:
//   - Cross-check trigger + last run summary
//   - Manual single-event injection (test helper; tagged is_simulated=TRUE)
//   - Reset simulated data
//   - Tunable thresholds (under-use %, auto-signout hours, etc.) — read-only
//     placeholder until /api/settings PATCH lands.
//
// The "Generate sample month" button has been removed per spec — real data
// arrives via the Bookings → Excel import flow.

import { useEffect, useState } from 'react';
import { api } from '../lib/api.js';
import { roundSignIn, roundSignOut } from '@iptbc/session-rounding';
import { fmtDate, fmtDateTime, fromDtLocalInput } from '../lib/datetime.js';
import { useAuth } from '../contexts/AuthContext.jsx';
import ImportButton from '../components/ImportButton.jsx';
import { useSettings } from '../contexts/SettingsContext.jsx';

const TODAY = new Date();
const DEFAULT_MONTH = `${TODAY.getFullYear()}-${String(TODAY.getMonth() + 1).padStart(2, '0')}`;

export default function Settings() {
  const [devices, setDevices] = useState([]);
  const [summary, setSummary] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);

  const [single, setSingle] = useState({
    device_id: '',
    cathay_id_entered: '',
    signin_at_utc: '',
    signout_at_utc: '',
  });
  const [ccMonth, setCcMonth] = useState(DEFAULT_MONTH);
  const [ccResult, setCcResult] = useState(null);

  // The Simulated test-data block is collapsed by default and gated on a
  // password re-prompt. Synthesised rows can skew Stats / Dashboard /
  // Cross-check numbers until they're wiped, so the UI makes it deliberate
  // to expose the controls.
  const [simUnlocked, setSimUnlocked] = useState(false);
  const [simUnlockBusy, setSimUnlockBusy] = useState(false);
  const [simUnlockErr, setSimUnlockErr] = useState(null);
  const unlockSimulator = async () => {
    const pw = prompt(
      'Enter your admin password to unlock the Simulated test-data section.\n\n' +
      'These controls inject rows that are reflected in Stats / Dashboard / Cross-check ' +
      'until you wipe them — use with caution.'
    );
    if (pw == null || pw === '') return;
    setSimUnlockBusy(true); setSimUnlockErr(null);
    try {
      await api.admin.verifyPassword(pw);
      setSimUnlocked(true);
    } catch (e) {
      setSimUnlockErr(e.message);
    } finally {
      setSimUnlockBusy(false);
    }
  };

  const refresh = async () => {
    try {
      const [d, s] = await Promise.all([api.devices.list(), api.simulate.summary()]);
      setDevices(d.devices);
      setSummary(s.summary);
    } catch (e) {
      setErr(e.message);
    }
  };
  useEffect(() => { refresh(); }, []);
  useEffect(() => {
    if (!single.device_id && devices.length > 0) {
      setSingle((s) => ({ ...s, device_id: devices[0].id }));
    }
  }, [devices]); // eslint-disable-line

  const injectSingle = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    try {
      const signinDate = fromDtLocalInput(single.signin_at_utc);
      const signoutDate = single.signout_at_utc ? fromDtLocalInput(single.signout_at_utc) : null;
      if (!signinDate) throw new Error('Invalid sign-in time');
      await api.simulate.sessions({
        sessions: [
          {
            device_id: single.device_id,
            cathay_id_entered: single.cathay_id_entered,
            signin_at_utc: signinDate.toISOString(),
            signout_at_utc: signoutDate ? signoutDate.toISOString() : null,
          },
        ],
      });
      alert('Inserted.');
      await refresh();
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  };

  // Live preview of the rounded slot times for the manual inject form.
  const signinDate = single.signin_at_utc ? fromDtLocalInput(single.signin_at_utc) : null;
  const signoutDate = single.signout_at_utc ? fromDtLocalInput(single.signout_at_utc) : null;
  const signinRounded = signinDate ? roundSignIn(signinDate) : null;
  const signoutRounded = signoutDate ? roundSignOut(signoutDate) : null;
  const runCrossCheck = async () => {
    setBusy(true);
    setErr(null);
    try {
      const r = await api.crossCheck.run({ month: ccMonth, includeSimulated: true });
      setCcResult(r);
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  };
  const reset = async () => {
    if (!confirm('Delete ALL simulated test rows? This does NOT touch real (uploaded) data.')) return;
    setBusy(true);
    try {
      await api.simulate.reset();
      setCcResult(null);
      await refresh();
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-semibold text-cathay-700">Settings</h1>
        <p className="text-sm text-slate-600 mt-1">
          Operational controls and test utilities. Real booking data is uploaded on the
          <strong> Bookings</strong> page.
        </p>
      </header>

      {err && <div className="bg-red-50 text-red-700 px-3 py-2 rounded text-sm">{err}</div>}

      <TimezoneSection />

      <AzureAdSection />

      <PasswordPolicySection />

      <SessionPolicySection />

      <FleetMtlsSection />

      <BackupSection />

      <ConfigBackupSection />

      <DangerZoneCalendar />

      <DangerZoneUsers />

      <section className="bg-white rounded shadow-sm border p-4">
        <h2 className="font-medium text-slate-800">Cross-check</h2>
        <p className="text-xs text-slate-500 mt-1">
          Runs the discrepancy algorithm for a given month. Detailed flag queue lives on the
          <strong> Cross-check</strong> page.
        </p>
        <div className="flex items-end gap-3 mt-3">
          <label className="text-sm">
            <div className="text-xs text-slate-600">Month</div>
            <input
              pattern="\d{4}-\d{2}"
              value={ccMonth}
              onChange={(e) => setCcMonth(e.target.value)}
              className="mt-1 border border-slate-300 rounded px-2 py-1.5"
            />
          </label>
          <button
            onClick={runCrossCheck}
            disabled={busy}
            className="bg-cathay-500 hover:bg-cathay-600 disabled:opacity-60 text-white px-3 py-2 rounded text-sm"
          >
            Run cross-check
          </button>
        </div>
        {ccResult && (
          <div className="mt-3 text-sm text-slate-700 grid grid-cols-2 sm:grid-cols-4 gap-2">
            <Tile label="No-shows" value={ccResult.summary.no_show} tone="red" />
            <Tile label="Substitutes" value={ccResult.summary.substitute} tone="amber" />
            <Tile label="Under-use" value={ccResult.summary.under_use} tone="amber" />
            <Tile label="Unbooked" value={ccResult.summary.unbooked} tone="slate" />
          </div>
        )}
      </section>

      <section className="bg-white rounded shadow-sm border p-4">
        <header className="flex items-start justify-between gap-3 flex-wrap">
          <div className="min-w-0">
            <h2 className="font-medium text-slate-800 flex items-center gap-2">
              Simulated test data
              {!simUnlocked && (
                <span className="text-[10px] uppercase tracking-wide bg-slate-100 text-slate-600 border border-slate-200 px-1.5 py-0.5 rounded">
                  Locked
                </span>
              )}
            </h2>
            <p className="text-xs text-slate-500 mt-1 max-w-prose">
              Inject historic sign-in/out events for verifying analytics before real iPads are in play.
              All rows are tagged <code>is_simulated = true</code> and can be wiped in one click.
            </p>
          </div>
          {!simUnlocked ? (
            <button
              onClick={unlockSimulator}
              disabled={simUnlockBusy}
              className="bg-cathay-500 hover:bg-cathay-600 disabled:opacity-60 text-white text-sm px-3 py-1.5 rounded shrink-0"
            >
              {simUnlockBusy ? 'Verifying…' : 'Unlock with password'}
            </button>
          ) : (
            <button
              onClick={() => setSimUnlocked(false)}
              className="text-xs text-slate-500 hover:text-slate-700 underline shrink-0"
            >
              Re-lock
            </button>
          )}
        </header>

        {!simUnlocked && (
          <div className="mt-3 bg-amber-50 border border-amber-200 rounded p-3 text-xs text-amber-900 max-w-prose">
            <strong>Warning — use with caution.</strong> The controls inside this section
            inject synthesised sessions and bookings into the live database. While those rows
            are tagged <code>is_simulated</code> and can be wiped, they ARE counted by Stats,
            the Dashboard widgets, IPT Utilisation, and the Cross-check algorithm until you
            wipe them — so the numbers you see on those pages will be a mix of real and
            simulated data. Only use this on a non-production environment or when you
            explicitly want to test analytics behaviour.
            {simUnlockErr && (
              <div className="mt-2 text-red-700">{simUnlockErr}</div>
            )}
          </div>
        )}

        {simUnlocked && (
          <>
        <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
          <Tile label="Simulated sessions" value={summary?.simulated_sessions ?? '—'} />
          <Tile label="Simulated bookings" value={summary?.simulated_bookings ?? '—'} />
          <Tile
            label="Range"
            value={
              summary?.earliest_session
                ? `${fmtDate(summary.earliest_session)} → ${fmtDate(summary.latest_session)}`
                : '—'
            }
          />
          <div className="self-end">
            <button onClick={reset} disabled={busy} className="text-sm text-red-600 hover:underline">
              Wipe simulated data
            </button>
          </div>
        </div>

        <MirrorBookingsToSessions onAfter={refresh} />

        <form onSubmit={injectSingle} className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
          <label className="block">
            <span className="text-xs text-slate-600">Device</span>
            <select
              required
              value={single.device_id}
              onChange={(e) => setSingle({ ...single, device_id: e.target.value })}
              className="mt-1 w-full border border-slate-300 rounded px-2 py-1.5"
            >
              {devices.map((d) => (
                <option key={d.id} value={d.id}>{d.name}</option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="text-xs text-slate-600">CathayID</span>
            <input
              required
              value={single.cathay_id_entered}
              onChange={(e) => setSingle({ ...single, cathay_id_entered: e.target.value })}
              className="mt-1 w-full border border-slate-300 rounded px-2 py-1.5"
            />
          </label>
          <label className="block">
            <span className="text-xs text-slate-600">Sign-in (24h)</span>
            <input
              required
              type="datetime-local"
              step="60"
              value={single.signin_at_utc}
              onChange={(e) => setSingle({ ...single, signin_at_utc: e.target.value })}
              className="mt-1 w-full border border-slate-300 rounded px-2 py-1.5"
            />
            {signinRounded && (
              <div className="text-[11px] text-slate-600 leading-tight mt-1">
                Actual: <span className="font-mono text-slate-800">{fmtDateTime(signinDate)}</span>
                <br />
                Slot start: <span className="font-mono text-cathay-700">{fmtDateTime(signinRounded.date)}</span>
              </div>
            )}
          </label>
          <label className="block">
            <span className="text-xs text-slate-600">Sign-out (24h, optional)</span>
            <input
              type="datetime-local"
              step="60"
              value={single.signout_at_utc}
              onChange={(e) => setSingle({ ...single, signout_at_utc: e.target.value })}
              className="mt-1 w-full border border-slate-300 rounded px-2 py-1.5"
            />
            {signoutRounded && (
              <div className="text-[11px] text-slate-600 leading-tight mt-1">
                Actual: <span className="font-mono text-slate-800">{fmtDateTime(signoutDate)}</span>
                <br />
                Slot end: <span className="font-mono text-cathay-700">{fmtDateTime(signoutRounded.date)}</span>
              </div>
            )}
          </label>
          <div className="col-span-full">
            <button
              disabled={busy || devices.length === 0}
              className="bg-cathay-500 hover:bg-cathay-600 disabled:opacity-60 text-white px-3 py-2 rounded text-sm"
            >
              Inject test session
            </button>
          </div>
        </form>
          </>
        )}
      </section>
    </div>
  );
}

/* ---------------- Timezone settings ---------------- */

// A curated set covering CX operating bases + common reference zones. Admin
// can still type any IANA name into the free-text field — the server
// validates via Intl.DateTimeFormat.
const TZ_PRESETS = [
  'Asia/Hong_Kong',
  'Asia/Shanghai',
  'Asia/Tokyo',
  'Asia/Singapore',
  'Asia/Bangkok',
  'Asia/Kolkata',
  'Asia/Dubai',
  'Australia/Sydney',
  'Europe/London',
  'Europe/Paris',
  'America/New_York',
  'America/Los_Angeles',
  'UTC',
];

function TimezoneSection() {
  const { user } = useAuth();
  const { timezone, refresh: refreshSettings } = useSettings();
  const isAdmin = user?.role === 'admin';
  const [value, setValue] = useState(timezone);
  const [free, setFree] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [ok, setOk] = useState(null);

  useEffect(() => { setValue(timezone); }, [timezone]);

  // Live preview using the chosen tz.
  const preview = (() => {
    try {
      const tz = (free || value).trim();
      const fmt = new Intl.DateTimeFormat('en-US', {
        timeZone: tz,
        year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit', hour12: false,
      });
      const parts = fmt.formatToParts(new Date()).reduce((acc, p) => {
        if (p.type !== 'literal') acc[p.type] = p.value;
        return acc;
      }, {});
      return `${parts.year}-${parts.month}-${parts.day} ${parts.hour}:${parts.minute}`;
    } catch {
      return 'Invalid IANA timezone';
    }
  })();

  const save = async (e) => {
    e.preventDefault();
    const target = (free || value).trim();
    if (!target) return;
    setBusy(true); setErr(null); setOk(null);
    try {
      await api.settings.patch('timezone', target);
      setOk(`Timezone updated to ${target}.`);
      setFree('');
      await refreshSettings();
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="bg-white rounded shadow-sm border p-4">
      <h2 className="font-medium text-slate-800">App timezone</h2>
      <p className="text-xs text-slate-500 mt-1">
        Universal time zone used for sign-in / sign-out rounding, calendar display, and
        cross-check date boundaries. All servers + clients read this value — change it once
        and the whole app follows.
      </p>
      <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
        <div>
          <div className="text-xs text-slate-500">Current</div>
          <div className="mt-1 inline-block font-mono px-2 py-1 bg-slate-100 rounded">{timezone}</div>
          <div className="text-xs text-slate-500 mt-2">
            Now in <span className="font-mono text-slate-800">{timezone}</span>:&nbsp;
            <span className="font-mono">
              {(() => {
                try {
                  const fmt = new Intl.DateTimeFormat('en-US', {
                    timeZone: timezone,
                    year: 'numeric', month: '2-digit', day: '2-digit',
                    hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
                  });
                  const p = fmt.formatToParts(new Date()).reduce((a, x) => (x.type !== 'literal' ? { ...a, [x.type]: x.value } : a), {});
                  return `${p.year}-${p.month}-${p.day} ${p.hour}:${p.minute}:${p.second}`;
                } catch {
                  return '—';
                }
              })()}
            </span>
          </div>
        </div>
        {isAdmin ? (
          <form onSubmit={save} className="space-y-2">
            <label className="block">
              <span className="text-xs text-slate-600">Pick a preset</span>
              <select
                value={free ? '' : value}
                onChange={(e) => { setFree(''); setValue(e.target.value); }}
                className="mt-1 w-full border border-slate-300 rounded px-2 py-1.5 text-sm"
              >
                {!TZ_PRESETS.includes(value) && <option value={value}>{value}</option>}
                {TZ_PRESETS.map((tz) => (<option key={tz} value={tz}>{tz}</option>))}
              </select>
            </label>
            <label className="block">
              <span className="text-xs text-slate-600">…or enter any IANA timezone</span>
              <input
                value={free}
                onChange={(e) => setFree(e.target.value)}
                placeholder="e.g. Asia/Hong_Kong"
                className="mt-1 w-full border border-slate-300 rounded px-2 py-1.5 text-sm font-mono"
              />
            </label>
            <div className="text-[11px] text-slate-600">
              Preview: <span className="font-mono">{preview}</span>
            </div>
            {err && <div className="text-sm text-red-600">{err}</div>}
            {ok && <div className="text-sm text-green-700">{ok}</div>}
            <button
              disabled={busy}
              className="bg-cathay-500 hover:bg-cathay-600 disabled:opacity-60 text-white px-3 py-1.5 rounded text-sm"
            >
              Save timezone
            </button>
          </form>
        ) : (
          <div className="text-xs text-slate-500">Only admins can change the app timezone.</div>
        )}
      </div>
    </section>
  );
}

/* ---------------- Backup / restore via CSV ---------------- */

function BackupSection() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const rows = [
    { key: 'devices', label: 'Devices', exportUrl: api.exports.devicesCsvUrl(), importFn: api.imports.devices },
    { key: 'system-users', label: 'System Users', exportUrl: api.exports.systemUsersCsvUrl(), importFn: api.imports.systemUsers, adminOnlyExport: true },
    { key: 'ipt-users', label: 'IPT Users', exportUrl: api.exports.iptUsersCsvUrl(), importFn: api.imports.iptUsers },
    { key: 'settings', label: 'Settings', exportUrl: api.exports.settingsCsvUrl(), importFn: api.imports.settings },
  ];
  return (
    <section className="bg-white rounded shadow-sm border p-4">
      <h2 className="font-medium text-slate-800">Backup & restore (CSV)</h2>
      <p className="text-xs text-slate-500 mt-1 max-w-prose">
        Round-trips for the four config tables. Useful for moving data between
        environments or as a partial restore. For a full point-in-time database
        backup, use{' '}
        <code className="font-mono">scripts/backup-postgres.sh</code> +{' '}
        <code className="font-mono">pg_restore</code> — that captures everything
        including session history, passwords, and passkeys.
      </p>
      <ul className="mt-3 divide-y divide-slate-100 text-sm">
        {rows.map((r) => (
          <li key={r.key} className="py-2 flex items-center justify-between gap-3">
            <div className="font-medium text-slate-700">{r.label}</div>
            <div className="flex gap-2">
              <a
                href={r.exportUrl}
                className="bg-white border border-slate-300 hover:bg-slate-50 text-slate-700 px-3 py-1.5 rounded text-xs"
              >
                Export CSV
              </a>
              {isAdmin && (
                <ImportButton onImport={(f) => r.importFn(f)} className="!px-3 !py-1.5 !text-xs">
                  Import CSV
                </ImportButton>
              )}
            </div>
          </li>
        ))}
      </ul>
      {!isAdmin && (
        <p className="text-xs text-slate-500 mt-2">Only admins can import data.</p>
      )}
    </section>
  );
}

function Tile({ label, value, tone }) {
  const tones = {
    red: 'text-red-700 bg-red-50',
    amber: 'text-amber-700 bg-amber-50',
    slate: 'text-slate-700 bg-slate-100',
  };
  return (
    <div className={'rounded p-2 ' + (tone ? tones[tone] : 'bg-slate-50')}>
      <div className="text-xs text-slate-500">{label}</div>
      <div className="text-lg font-medium">{value}</div>
    </div>
  );
}

/* ---------------- Mirror booked sessions → actual usage ---------------- */
/**
 * Synthesise an actual sign-in/sign-out session for every committed booking
 * BLOCK (consecutive-slot run for the same pilot+IPT). Useful for shaking
 * down the Stats / Dashboard / Cross-check pages before real iPads are
 * paired and live.
 *
 * - Sessions are tagged is_simulated=true so the "Wipe simulated data"
 *   button above clears them.
 * - "Replace" mode wipes existing simulated sessions first so repeated
 *   clicks don't accumulate duplicates.
 * - Optional month scope.
 */
function MirrorBookingsToSessions({ onAfter }) {
  const TODAY_M = `${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, '0')}`;
  const [scope, setScope] = useState('all');     // 'all' | 'month'
  const [month, setMonth] = useState(TODAY_M);
  const [overwrite, setOverwrite] = useState(true);
  const [includeStaged, setIncludeStaged] = useState(true);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState(null);

  const run = async () => {
    if (!confirm(
      'Convert every booking into a matching simulated session?\n\n' +
      (overwrite ? 'This will FIRST wipe all existing simulated sessions to avoid duplicates.' : '')
    )) return;
    setBusy(true); setErr(null); setResult(null);
    try {
      const body = { overwrite, include_staged: includeStaged };
      if (scope === 'month') body.month = month;
      const r = await api.simulate.bookingsToSessions(body);
      setResult(r);
      onAfter?.();
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-4 border-t border-slate-100 pt-4">
      <div className="text-sm font-medium text-slate-700">
        Mirror booked sessions → actual usage
      </div>
      <p className="text-xs text-slate-500 mt-0.5 max-w-prose">
        For each committed booking block, write a matching iPad sign-in / sign-out session
        with times equal to the booked start and end. Useful to populate Stats / Dashboard /
        Cross-check pages with a "perfect attendance" baseline. All synthesised rows are
        <code className="ml-1">is_simulated</code>.
      </p>
      <div className="mt-3 grid grid-cols-1 sm:grid-cols-4 gap-3 text-sm items-end">
        <label className="block">
          <span className="text-xs text-slate-600">Scope</span>
          <select
            value={scope} onChange={(e) => setScope(e.target.value)}
            className="mt-1 w-full border border-slate-300 rounded px-2 py-1.5"
          >
            <option value="all">All months</option>
            <option value="month">One month</option>
          </select>
        </label>
        {scope === 'month' && (
          <label className="block">
            <span className="text-xs text-slate-600">Month (YYYY-MM)</span>
            <input
              pattern="\d{4}-\d{2}" value={month}
              onChange={(e) => setMonth(e.target.value)}
              className="mt-1 w-full border border-slate-300 rounded px-2 py-1.5"
            />
          </label>
        )}
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={overwrite}
            onChange={(e) => setOverwrite(e.target.checked)}
          />
          <span>Replace existing simulated sessions first</span>
        </label>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={includeStaged}
            onChange={(e) => setIncludeStaged(e.target.checked)}
          />
          <span>Include staged (un-applied) bookings</span>
        </label>
        <button
          onClick={run} disabled={busy}
          className="bg-cathay-500 hover:bg-cathay-600 disabled:opacity-60 text-white px-3 py-1.5 rounded text-sm h-fit"
        >
          {busy ? 'Mirroring…' : 'Mirror bookings → sessions'}
        </button>
      </div>
      {err && <div className="mt-2 text-sm text-red-600">{err}</div>}
      {result && (
        <div className="mt-2 text-xs space-y-0.5">
          <div className={result.inserted > 0 ? 'text-green-700' : 'text-amber-700'}>
            Created <strong>{result.inserted}</strong> simulated session{result.inserted === 1 ? '' : 's'}
            from <strong>{result.total_blocks}</strong> booking block{result.total_blocks === 1 ? '' : 's'}.
            {result.overwrote ? ' Existing simulated sessions were cleared first.' : ''}
          </div>
          <div className="text-slate-500">
            Source bookings considered: {result.source_bookings}
            {result.staged_bookings > 0 && ` (${result.staged_bookings} staged${result.include_staged ? ', included' : ', SKIPPED — tick "Include staged" to mirror them too'})`}.
            {result.skipped_no_device > 0
              ? ` ${result.skipped_no_device} block(s) skipped: booking had no device pinned.`
              : ''}
          </div>
          {result.inserted === 0 && result.source_bookings === 0 && (
            <div className="text-red-600">
              No bookings found in scope. Make sure your CrewLink uploads have been applied
              on the Bookings page (or tick "Include staged" above).
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ---------------- Azure AD / Microsoft SSO ---------------- */

function AzureAdSection() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [cfg, setCfg] = useState(null);
  const [form, setForm] = useState({ tenant_id: '', client_id: '', redirect_uri: '', client_secret: '' });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [msg, setMsg] = useState(null);
  const [revealSecret, setRevealSecret] = useState(false);

  const refresh = async () => {
    try {
      const r = await api.admin.azureConfig();
      setCfg(r);
      setForm({
        tenant_id:     r.tenant_id     || '',
        client_id:     r.client_id     || '',
        redirect_uri:  r.redirect_uri  || '',
        client_secret: '',
      });
    } catch (e) { setErr(e.message); }
  };
  useEffect(() => { if (isAdmin) refresh(); }, [isAdmin]);

  const save = async () => {
    setBusy(true); setErr(null); setMsg(null);
    try {
      const body = {
        tenant_id:    form.tenant_id.trim(),
        client_id:    form.client_id.trim(),
        redirect_uri: form.redirect_uri.trim(),
      };
      // Only send a non-empty secret. Empty input means "keep stored value".
      if (form.client_secret.trim() !== '') body.client_secret = form.client_secret;
      await api.admin.azureConfigPatch(body);
      setMsg('Azure AD configuration saved.');
      setForm((f) => ({ ...f, client_secret: '' }));
      await refresh();
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  };
  const clearSecret = async () => {
    if (!confirm('Clear the stored Azure client secret? The Microsoft sign-in button will be disabled until you set a new secret.')) return;
    setBusy(true); setErr(null); setMsg(null);
    try {
      await api.admin.azureConfigPatch({ client_secret: '__CLEAR__' });
      setMsg('Azure client secret cleared.');
      await refresh();
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  };

  if (!isAdmin) {
    return (
      <section className="bg-white rounded shadow-sm border p-4">
        <h2 className="font-medium text-slate-800">Azure AD / Microsoft SSO</h2>
        <p className="text-xs text-slate-500 mt-1">Only admins can view or change Azure AD configuration.</p>
      </section>
    );
  }

  const fallback = cfg?.env_fallback || {};
  const effectiveSet = Boolean(cfg?.client_id || fallback.client_id);

  return (
    <section className="bg-white rounded shadow-sm border p-4">
      <h2 className="font-medium text-slate-800">Azure AD / Microsoft SSO</h2>
      <p className="text-xs text-slate-500 mt-1 max-w-prose">
        Credentials for the "Sign in with Microsoft" button. Values stored here override
        the matching <code>AZURE_*</code> environment variables. The client secret is
        never returned by the API — leave the field blank to keep the stored value.
      </p>

      {err && <div className="mt-2 text-sm text-red-600">{err}</div>}
      {msg && <div className="mt-2 text-sm text-green-700">{msg}</div>}

      <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
        <label className="block">
          <span className="text-xs text-slate-600">Tenant ID</span>
          <input
            value={form.tenant_id}
            onChange={(e) => setForm({ ...form, tenant_id: e.target.value })}
            placeholder={fallback.tenant_id || 'e.g. 11111111-2222-3333-4444-555555555555'}
            className="mt-1 w-full border border-slate-300 rounded px-2 py-1.5 font-mono text-xs"
          />
          {!form.tenant_id && fallback.tenant_id && (
            <div className="text-[11px] text-slate-500 mt-1">Falls back to env: <span className="font-mono">{fallback.tenant_id}</span></div>
          )}
        </label>

        <label className="block">
          <span className="text-xs text-slate-600">Client ID (Application ID)</span>
          <input
            value={form.client_id}
            onChange={(e) => setForm({ ...form, client_id: e.target.value })}
            placeholder={fallback.client_id || 'e.g. aaaaaaaa-bbbb-cccc-dddd-eeeeeeeeeeee'}
            className="mt-1 w-full border border-slate-300 rounded px-2 py-1.5 font-mono text-xs"
          />
          {!form.client_id && fallback.client_id && (
            <div className="text-[11px] text-slate-500 mt-1">Falls back to env: <span className="font-mono">{fallback.client_id}</span></div>
          )}
        </label>

        <label className="block sm:col-span-2">
          <span className="text-xs text-slate-600">Redirect URI</span>
          <input
            value={form.redirect_uri}
            onChange={(e) => setForm({ ...form, redirect_uri: e.target.value })}
            placeholder={fallback.redirect_uri || 'https://ipt.cxfop.com/api/azure-auth/callback'}
            className="mt-1 w-full border border-slate-300 rounded px-2 py-1.5 font-mono text-xs"
          />
          {!form.redirect_uri && fallback.redirect_uri && (
            <div className="text-[11px] text-slate-500 mt-1">Falls back to env: <span className="font-mono">{fallback.redirect_uri}</span></div>
          )}
        </label>

        <label className="block sm:col-span-2">
          <span className="text-xs text-slate-600">
            Client secret
            <span className={
              'ml-2 inline-block text-[10px] px-1.5 py-0.5 rounded ' +
              (cfg?.client_secret_set
                ? 'bg-green-50 text-green-700 border border-green-200'
                : fallback.client_secret_set
                  ? 'bg-amber-50 text-amber-700 border border-amber-200'
                  : 'bg-slate-100 text-slate-600 border border-slate-200')
            }>
              {cfg?.client_secret_set
                ? 'STORED'
                : fallback.client_secret_set
                  ? 'USING ENV FALLBACK'
                  : 'NOT SET'}
            </span>
          </span>
          <div className="mt-1 flex gap-2">
            <input
              type={revealSecret ? 'text' : 'password'}
              value={form.client_secret}
              onChange={(e) => setForm({ ...form, client_secret: e.target.value })}
              placeholder={cfg?.client_secret_set ? '(leave blank to keep stored value)' : 'paste the secret value, not the secret ID'}
              className="flex-1 border border-slate-300 rounded px-2 py-1.5 font-mono text-xs"
              autoComplete="off"
            />
            <button
              type="button"
              onClick={() => setRevealSecret((v) => !v)}
              className="text-xs px-2 py-1 border border-slate-300 rounded hover:bg-slate-50"
              title="Toggle visibility"
            >
              {revealSecret ? 'Hide' : 'Show'}
            </button>
          </div>
        </label>
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <button
          onClick={save} disabled={busy}
          className="bg-cathay-500 hover:bg-cathay-600 disabled:opacity-60 text-white px-3 py-1.5 rounded text-sm"
        >
          Save Azure configuration
        </button>
        {cfg?.client_secret_set && (
          <button
            onClick={clearSecret} disabled={busy}
            className="text-sm text-red-600 hover:underline ml-2"
          >
            Clear stored secret
          </button>
        )}
        <div className="ml-auto text-[11px] text-slate-500">
          Status: {effectiveSet && (cfg?.client_secret_set || fallback.client_secret_set)
            ? <span className="text-green-700 font-medium">Sign-in with Microsoft is enabled</span>
            : <span className="text-amber-700 font-medium">Disabled — fill all four fields to enable</span>}
        </div>
      </div>
    </section>
  );
}

/* ---------------- Password policy + MFA enforcement ---------------- */

function PasswordPolicySection() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [rows, setRows] = useState({});
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [err, setErr] = useState(null);

  const refresh = async () => {
    try {
      const r = await api.settings.list();
      const map = Object.fromEntries(r.settings.map((s) => [s.key, s.value]));
      setRows(map);
    } catch (e) { setErr(e.message); }
  };
  useEffect(() => { refresh(); }, []);

  const update = (key, value) => setRows((r) => ({ ...r, [key]: value }));

  const save = async () => {
    setBusy(true); setErr(null); setMsg(null);
    try {
      const keys = [
        'password_min_length',
        'password_require_alpha',
        'password_require_digit',
        'password_require_symbol',
        'password_expiry_days',
        'mfa_required',
      ];
      for (const k of keys) {
        if (rows[k] != null) await api.settings.patch(k, String(rows[k]));
      }
      setMsg('Password policy saved.');
      await refresh();
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="bg-white rounded shadow-sm border p-4">
      <h2 className="font-medium text-slate-800">User Administration — password &amp; MFA policy</h2>
      <p className="text-xs text-slate-500 mt-1 max-w-prose">
        Applied to <em>new</em> passwords (initial setup, admin reset, self-service change).
        Existing hashes aren't re-checked retroactively — users only feel a rule
        change the next time they pick a password.{' '}
        <span className="text-slate-600 font-medium">
          All passwords are stored as bcrypt hashes (cost factor 12) — never as plaintext.
        </span>
      </p>
      <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
        <label className="block">
          <span className="text-xs text-slate-600">Minimum length (6–128)</span>
          <input
            type="number" min={6} max={128}
            value={rows.password_min_length ?? ''}
            disabled={!isAdmin}
            onChange={(e) => update('password_min_length', e.target.value)}
            className="mt-1 w-full border border-slate-300 rounded px-2 py-1.5 disabled:bg-slate-50"
          />
        </label>
        <label className="block">
          <span className="text-xs text-slate-600">Password expiry days (0 = never)</span>
          <input
            type="number" min={0} max={3650}
            value={rows.password_expiry_days ?? ''}
            disabled={!isAdmin}
            onChange={(e) => update('password_expiry_days', e.target.value)}
            className="mt-1 w-full border border-slate-300 rounded px-2 py-1.5 disabled:bg-slate-50"
          />
        </label>
        <PolicyToggle
          label="Require at least one letter (A–Z, a–z)"
          value={rows.password_require_alpha} onChange={(v) => update('password_require_alpha', v)}
          disabled={!isAdmin}
        />
        <PolicyToggle
          label="Require at least one digit (0–9)"
          value={rows.password_require_digit} onChange={(v) => update('password_require_digit', v)}
          disabled={!isAdmin}
        />
        <PolicyToggle
          label="Require at least one symbol (e.g. !@#$%)"
          value={rows.password_require_symbol} onChange={(v) => update('password_require_symbol', v)}
          disabled={!isAdmin}
        />
        <label className="block">
          <span className="text-xs text-slate-600">Multi-factor enforcement</span>
          <select
            value={rows.mfa_required ?? 'optional'}
            disabled={!isAdmin}
            onChange={(e) => update('mfa_required', e.target.value)}
            className="mt-1 w-full border border-slate-300 rounded px-2 py-1.5 disabled:bg-slate-50"
          >
            <option value="none">None — password only</option>
            <option value="optional">Optional — user can enable TOTP / passkey</option>
            <option value="required">Required — every user must set up TOTP</option>
            <option value="passkey_required">Passkey required — every user must register a passkey</option>
          </select>
        </label>
      </div>
      {err && <div className="mt-2 text-sm text-red-600">{err}</div>}
      {msg && <div className="mt-2 text-sm text-green-700">{msg}</div>}
      {isAdmin && (
        <div className="mt-3">
          <button
            onClick={save} disabled={busy}
            className="bg-cathay-500 hover:bg-cathay-600 disabled:opacity-60 text-white px-3 py-1.5 rounded text-sm"
          >
            Save password &amp; MFA policy
          </button>
        </div>
      )}
    </section>
  );
}

function PolicyToggle({ label, value, onChange, disabled }) {
  const b = value === 'true' || value === true;
  return (
    <label className="flex items-center gap-2 text-sm">
      <input
        type="checkbox" checked={b} disabled={disabled}
        onChange={(e) => onChange(e.target.checked ? 'true' : 'false')}
      />
      <span className={disabled ? 'text-slate-500' : ''}>{label}</span>
    </label>
  );
}

/* ---------------- IPT session policy (auto-signout timeout) ---------------- */

function SessionPolicySection() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [hours, setHours] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [err, setErr] = useState(null);

  const refresh = async () => {
    try {
      const r = await api.settings.list();
      const row = r.settings.find((s) => s.key === 'auto_signout_hours');
      setHours(row?.value ?? '4');
    } catch (e) {
      setErr(e.message);
    }
  };
  useEffect(() => { refresh(); }, []);

  const save = async () => {
    setBusy(true); setErr(null); setMsg(null);
    try {
      const n = parseInt(hours, 10);
      if (!Number.isInteger(n) || n < 1 || n > 24) {
        throw new Error('Auto-signout hours must be a whole number between 1 and 24.');
      }
      await api.settings.patch('auto_signout_hours', String(n));
      setMsg(`Auto-signout timeout set to ${n} hour${n === 1 ? '' : 's'}.`);
      await refresh();
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="bg-white rounded shadow-sm border p-4">
      <h2 className="font-medium text-slate-800">IPT session policy</h2>
      <p className="text-xs text-slate-500 mt-1 max-w-prose">
        How long an open session can stay open before the server force-closes
        it. The cron runs every 5 minutes and closes any session whose
        sign-in time is older than this many hours, marking it as{' '}
        <code className="font-mono">auto_closed=TRUE</code> so the cross-check
        page can flag potential abuse.
      </p>
      <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
        <label className="block">
          <span className="text-xs text-slate-600">Auto-signout after (1–24 hours)</span>
          <input
            type="number" min={1} max={24} step={1}
            value={hours}
            disabled={!isAdmin}
            onChange={(e) => setHours(e.target.value)}
            className="mt-1 w-full border border-slate-300 rounded px-2 py-1.5 disabled:bg-slate-50"
          />
        </label>
      </div>
      {err && <div className="mt-2 text-sm text-red-600">{err}</div>}
      {msg && <div className="mt-2 text-sm text-green-700">{msg}</div>}
      {isAdmin && (
        <div className="mt-3">
          <button
            onClick={save} disabled={busy}
            className="bg-cathay-500 hover:bg-cathay-600 disabled:opacity-60 text-white px-3 py-1.5 rounded text-sm"
          >
            Save session policy
          </button>
        </div>
      )}
    </section>
  );
}

/* ---------------- Config backup (settings JSON + calendar JSON) ---------------- */

function ConfigBackupSection() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [msg, setMsg] = useState(null);

  const onImport = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    const password = prompt('Confirm with your admin password to import configuration:');
    if (!password) return;
    setBusy(true); setErr(null); setMsg(null);
    try {
      const text = await file.text();
      const parsed = JSON.parse(text);
      if (!parsed.settings || typeof parsed.settings !== 'object')
        throw new Error('Invalid config file — missing "settings" object.');
      const r = await api.admin.configImport(parsed.settings, password);
      setMsg(`Imported ${r.upserted} settings.`);
    } catch (e) {
      setErr(e.message);
    } finally { setBusy(false); }
  };

  return (
    <section className="bg-white rounded shadow-sm border p-4">
      <h2 className="font-medium text-slate-800">Configuration &amp; calendar backup (JSON)</h2>
      <p className="text-xs text-slate-500 mt-1 max-w-prose">
        Round-trip the full <strong>settings</strong> table as a JSON blob, or download a
        snapshot of the calendar data (devices, IPT users, bookings, sessions, cross-check
        runs and flags). Useful for cross-environment diffs or a manual restore drill.
        For a true point-in-time database backup use <code>scripts/backup-postgres.sh</code>.
      </p>
      {err && <div className="mt-2 text-sm text-red-600">{err}</div>}
      {msg && <div className="mt-2 text-sm text-green-700">{msg}</div>}
      <div className="mt-3 flex flex-wrap gap-2 text-sm">
        {isAdmin && (
          <a
            href={api.admin.configExportUrl()}
            className="bg-white border border-slate-300 hover:bg-slate-50 text-slate-700 px-3 py-1.5 rounded"
          >
            Export configuration
          </a>
        )}
        {isAdmin && (
          <label className="inline-block">
            <input type="file" accept="application/json,.json" onChange={onImport} className="hidden" />
            <span className="bg-white border border-slate-300 hover:bg-slate-50 text-slate-700 px-3 py-1.5 rounded cursor-pointer">
              {busy ? 'Importing…' : 'Import configuration'}
            </span>
          </label>
        )}
        {isAdmin && (
          <a
            href={api.admin.calendarBackupUrl()}
            className="bg-white border border-slate-300 hover:bg-slate-50 text-slate-700 px-3 py-1.5 rounded"
          >
            Download calendar backup
          </a>
        )}
      </div>
      {!isAdmin && <p className="text-xs text-slate-500 mt-2">Only admins can export or import configuration.</p>}
    </section>
  );
}

/* ---------------- Danger zone — calendar deletion ---------------- */

function DangerZoneCalendar() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [devices, setDevices] = useState([]);
  const [scope, setScope] = useState('month');
  const [month, setMonth] = useState(
    `${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, '0')}`
  );
  const [deviceId, setDeviceId] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [msg, setMsg] = useState(null);

  useEffect(() => {
    api.devices.list().then((r) => setDevices(r.devices)).catch(() => {});
  }, []);

  const needsMonth = scope === 'month' || scope === 'device_month';
  const needsDevice = scope === 'device' || scope === 'device_month';

  const submit = async () => {
    const password = prompt(
      `Type your admin password to confirm. This deletes ${scope === 'all' ? 'EVERY booking + EVERY session' :
        scope === 'month' ? `every booking + session in ${month}` :
        scope === 'device' ? `every booking + session pinned to the chosen IPT` :
        `every booking + session pinned to the chosen IPT in ${month}`}.`
    );
    if (!password) return;
    setBusy(true); setErr(null); setMsg(null);
    try {
      const r = await api.admin.destroyCalendar({
        scope,
        month: needsMonth ? month : undefined,
        device_id: needsDevice ? deviceId : undefined,
        password,
      });
      setMsg(`Deleted ${r.sessionsDeleted} sessions and ${r.bookingsDeleted} bookings.`);
    } catch (e) {
      setErr(e.message);
    } finally { setBusy(false); }
  };

  if (!isAdmin) return null;

  return (
    <section className="bg-white rounded shadow-sm border border-red-200 p-4">
      <h2 className="font-medium text-red-700">Danger zone — calendar deletion</h2>
      <p className="text-xs text-red-700/80 mt-1 max-w-prose">
        Bulk-delete calendar data. Each click is followed by a password prompt and audit-logged.
        These operations are <strong>irreversible</strong> from the UI — use the JSON backup or
        <code> pg_restore</code> to recover.
      </p>
      <div className="mt-3 grid grid-cols-1 sm:grid-cols-4 gap-3 text-sm items-end">
        <label className="block">
          <span className="text-xs text-slate-600">Scope</span>
          <select
            value={scope} onChange={(e) => setScope(e.target.value)}
            className="mt-1 w-full border border-slate-300 rounded px-2 py-1.5"
          >
            <option value="month">One month — all IPTs</option>
            <option value="device_month">One month — one IPT</option>
            <option value="device">All months — one IPT</option>
            <option value="all">EVERYTHING (all IPTs, all months)</option>
          </select>
        </label>
        {needsMonth && (
          <label className="block">
            <span className="text-xs text-slate-600">Month (YYYY-MM)</span>
            <input
              pattern="\d{4}-\d{2}" value={month}
              onChange={(e) => setMonth(e.target.value)}
              className="mt-1 w-full border border-slate-300 rounded px-2 py-1.5"
            />
          </label>
        )}
        {needsDevice && (
          <label className="block">
            <span className="text-xs text-slate-600">IPT device</span>
            <select
              value={deviceId} onChange={(e) => setDeviceId(e.target.value)}
              className="mt-1 w-full border border-slate-300 rounded px-2 py-1.5"
            >
              <option value="">— select —</option>
              {devices.map((d) => (
                <option key={d.id} value={d.id}>{d.name}</option>
              ))}
            </select>
          </label>
        )}
        <button
          onClick={submit}
          disabled={busy || (needsDevice && !deviceId)}
          className="bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white px-3 py-1.5 rounded text-sm h-fit"
        >
          {busy ? 'Deleting…' : 'Delete (requires password)'}
        </button>
      </div>
      {err && <div className="mt-2 text-sm text-red-600">{err}</div>}
      {msg && <div className="mt-2 text-sm text-green-700">{msg}</div>}
    </section>
  );
}

/* ---------------- Danger zone — user wipes ---------------- */

function DangerZoneUsers() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [msg, setMsg] = useState(null);

  if (!isAdmin) return null;

  const wipeIpt = async () => {
    const password = prompt('Confirm with your admin password to WIPE the IPT user directory:');
    if (!password) return;
    setBusy(true); setErr(null); setMsg(null);
    try {
      const r = await api.admin.destroyIptUsers(password);
      setMsg(`Wiped ${r.deleted} IPT users. They'll reappear automatically if their CathayID is seen again.`);
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  };
  const wipeSystem = async () => {
    const password = prompt('Confirm with your admin password to WIPE all System Users (except the initial admin):');
    if (!password) return;
    setBusy(true); setErr(null); setMsg(null);
    try {
      const r = await api.admin.destroySystemUsers(password);
      setMsg(`Wiped ${r.deleted} System Users. Kept user ids: ${r.kept.join(', ')}.`);
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  };

  return (
    <section className="bg-white rounded shadow-sm border border-red-200 p-4">
      <h2 className="font-medium text-red-700">Danger zone — user wipes</h2>
      <p className="text-xs text-red-700/80 mt-1 max-w-prose">
        Both operations require your admin password. System User wipe always preserves the
        initial bootstrap user (the one created during first /setup) so you don't lock
        yourself out.
      </p>
      {err && <div className="mt-2 text-sm text-red-600">{err}</div>}
      {msg && <div className="mt-2 text-sm text-green-700">{msg}</div>}
      <div className="mt-3 flex flex-wrap gap-2 text-sm">
        <button
          onClick={wipeIpt} disabled={busy}
          className="bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white px-3 py-1.5 rounded"
        >
          Wipe all IPT Users
        </button>
        <button
          onClick={wipeSystem} disabled={busy}
          className="bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white px-3 py-1.5 rounded"
        >
          Wipe System Users (except initial)
        </button>
      </div>
    </section>
  );
}

/* ---------------- Fleet mTLS profile (MDM-friendly) ---------------- */

function FleetMtlsSection() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [info, setInfo] = useState(null); // { fingerprint, issued_at, expires_at }
  const [reveal, setReveal] = useState(null); // { password }
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const refresh = async () => {
    try {
      const r = await api.admin.mtlsFleetGet();
      setInfo(r);
    } catch (e) { setErr(e.message); }
  };
  useEffect(() => { refresh(); }, []);

  const issue = async () => {
    if (!confirm(
      'Generate a new fleet mTLS profile?\n\n' +
      'This INVALIDATES the previous fleet certificate immediately on the ' +
      'server. Every iPad already using the fleet cert will need the new ' +
      'profile pushed via MDM before it can complete the cold-start handshake.'
    )) return;
    setBusy(true);
    setErr(null);
    try {
      const { blob, p12Password } = await api.admin.mtlsFleetIssue();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'iptbookingcheck-fleet.mobileconfig';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      setReveal({ password: p12Password });
      await refresh();
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  };

  const fpShort = info?.fingerprint
    ? `${info.fingerprint.slice(0, 8)}…${info.fingerprint.slice(-8)}`
    : null;
  const expiresAt = info?.expires_at ? new Date(info.expires_at) : null;
  const daysLeft = expiresAt ? Math.floor((expiresAt - Date.now()) / 86_400_000) : null;

  return (
    <section className="bg-white rounded shadow-sm border p-4">
      <h2 className="font-medium text-slate-800">Fleet mTLS profile (MDM)</h2>
      <p className="text-xs text-slate-500 mt-1 max-w-prose">
        One shared client certificate for every iPad in the fleet — push it
        via Jamf / Intune / Workspace ONE. The cert proves "this is one of
        our paired iPads" at the nginx layer; the per-iPad identity still
        comes from the device token stored in IndexedDB. Per-device
        revocation falls back to revoking the device row in the Devices
        page. Regenerating the fleet profile <strong>invalidates the
        previous one server-side immediately</strong>, so all iPads must
        receive the new profile before they next cold-start.
      </p>
      <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
        <div className="rounded bg-slate-50 p-3">
          <div className="text-xs text-slate-500">Fingerprint</div>
          <div className="font-mono text-slate-800 mt-1">
            {fpShort || <span className="text-slate-400">Not issued yet</span>}
          </div>
        </div>
        <div className="rounded bg-slate-50 p-3">
          <div className="text-xs text-slate-500">Issued</div>
          <div className="text-slate-800 mt-1">
            {info?.issued_at ? new Date(info.issued_at).toLocaleString() : <span className="text-slate-400">—</span>}
          </div>
        </div>
        <div className="rounded bg-slate-50 p-3">
          <div className="text-xs text-slate-500">Expires</div>
          <div className="text-slate-800 mt-1">
            {expiresAt ? expiresAt.toLocaleDateString() : <span className="text-slate-400">—</span>}
            {daysLeft != null && (
              <span className={
                'ml-2 text-xs ' +
                (daysLeft < 0 ? 'text-red-700' : daysLeft < 30 ? 'text-amber-700' : 'text-slate-500')
              }>
                ({daysLeft < 0 ? 'expired' : `${daysLeft} day${daysLeft === 1 ? '' : 's'} left`})
              </span>
            )}
          </div>
        </div>
      </div>

      {reveal && (
        <div className="mt-3 bg-cyan-50 border border-cyan-200 rounded p-4 text-sm space-y-2">
          <div className="font-medium text-cyan-900">Fleet profile downloaded.</div>
          <div className="text-cyan-900">
            Upload <strong>iptbookingcheck-fleet.mobileconfig</strong> to your MDM
            as a configuration profile, and ship it to the iPads. Each iPad's
            install prompt will ask for the PKCS#12 password below — pre-fill
            it in MDM if your tooling supports that, otherwise read it aloud
            during install:
          </div>
          <div className="text-3xl font-mono tracking-widest text-cyan-900 select-all">
            {reveal.password}
          </div>
          <div className="text-cyan-700 text-xs">
            The password is shown <strong>only once</strong>. It is not stored
            anywhere on the server. Lose it and you'll have to re-issue the
            profile.
          </div>
          <button
            onClick={() => setReveal(null)}
            className="text-xs text-cyan-700 hover:underline"
          >
            Dismiss
          </button>
        </div>
      )}

      {err && <div className="mt-2 text-sm text-red-600">{err}</div>}
      {isAdmin && (
        <div className="mt-3">
          <button
            onClick={issue} disabled={busy}
            className="bg-cathay-500 hover:bg-cathay-600 disabled:opacity-60 text-white px-3 py-1.5 rounded text-sm"
          >
            {info?.fingerprint ? 'Re-generate fleet profile' : 'Generate fleet profile'}
          </button>
        </div>
      )}
    </section>
  );
}
